import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { UntypedFormBuilder, Validators, UntypedFormGroup } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import uuid from '../../../utils/uuid';
import { TransactionService } from '../../../api/transaction.service';
import { DealFileChecklistService } from '../../../api/deal-file-checklist.service';
import { FeeSplitsService } from '../../../api/fee-splits.service';
import { TranslateService } from '../../../api/translate.service';
import { FormService } from './../../../api/form.service';
import { DataObject } from '../../../interfaces/data-objects';
import { TransactionType } from '../../../models/transactions-enum';
import { Countries } from '../../../utils/countries-enum';

@UntilDestroy()
@Component({
  selector: 'iv-deal-file-checklist',
  templateUrl: './deal-file-checklist.component.html',
  styleUrls: ['./deal-file-checklist.component.scss']
})
export class DealFileChecklistComponent implements OnInit, OnDestroy {
  @Input() data;
  @Input() transaction;
  @Input() header = '';
  @Input() padding = false;
  @Input() disabled = false;
  @Input() userCountry: Countries;
  @Input() hideDeleteDocumentsBtn = false;
  
  @Output() saveDealFileChecklist: EventEmitter<DataObject>;
  @Output() loadTransactionEmitter: EventEmitter<DataObject>;
  @Output() downloadFile: EventEmitter<DataObject>;
  @Output() saveTransaction: EventEmitter<DataObject>;

  uuid = uuid();
  dealFileChecklistForm: UntypedFormGroup;
  questionsArray = [];
  isExternalBroker = false;
  feeSplits: DataObject[];

  constructor(
    private fb: UntypedFormBuilder,
    private formService: FormService,
    private transactionService: TransactionService,
    private dealFileChecklistService: DealFileChecklistService,
    private feeSplitService: FeeSplitsService,
    private translateService: TranslateService) {
    this.saveDealFileChecklist = new EventEmitter();
    this.loadTransactionEmitter = new EventEmitter();
    this.downloadFile = new EventEmitter();
    this.saveTransaction = new EventEmitter();
  }

  ngOnInit() {
    this.getQuestionsArray();
    this.setupForm();
  }

  setupForm() {
    if (this.questionsArray) {
      const dealFileChecklistObject = {};
      this.questionsArray.map(question => {
        dealFileChecklistObject[question.identifier] = [{
          value: this.data[question.identifier],
          disabled: this.disabled
        }, Validators.required];
        if (question.identifier !== 'go_related_client') {
          dealFileChecklistObject[question.identifier + '_note'] = [{
            value: this.data[question.identifier + '_note'] || '',
            disabled: this.disabled
          }, Validators.required];
        }
      });

      this.dealFileChecklistForm = this.fb.group(dealFileChecklistObject);
      this.formService.addLoadedForm(this.dealFileChecklistForm, this.uuid);
      this.setupDealFileChecklistSubscription();
    }
  }

  setupDealFileChecklistSubscription() {
    this.dealFileChecklistForm.valueChanges
      .pipe(
        untilDestroyed(this),
        debounceTime(850),
        distinctUntilChanged()
      )
      .subscribe(changes => {
        if (changes.conflict_of_interest !== undefined && !changes.conflict_of_interest) {
          changes.conflict_of_interest_note = '';
          this.updateView({ 'conflict_of_interest_note': '' });
        }
        if (changes.government_entity || changes.government_entity === null) {
          changes.government_entity_note = '';
          this.updateView({ 'government_entity_note': '' });
        }
        if (changes.signed_agency_agreement) {
          changes.signed_agency_agreement_note = '';
          this.updateView({ 'signed_agency_agreement_note': '' });
        }
        if (changes.executed_lease_agreement) {
          changes.executed_lease_agreement_note = '';
          this.updateView({ 'executed_lease_agreement_note': '' });
        }
        if (changes.supporting_correspondence) {
          changes.supporting_correspondence_note = '';
          this.updateView({ 'supporting_correspondence_note': '' });
        }
        if (changes.fee_calculation_approval !== 'false') {
          changes.fee_calculation_approval_note = '';
          this.updateView({ 'fee_calculation_approval_note': '' });
        }
        if (changes.supporting_documents_upload) {
          changes.supporting_documents_upload_note = '';
          this.updateView({ 'supporting_documents_upload_note': '' });
        }
        if (changes.conjunctional_agreement_uploaded) {
          changes.conjunctional_agreement_uploaded_note = '';
          this.updateView({ 'conjunctional_agreement_uploaded_note': '' });
        }
        if (changes.signed_dsf_mandate) {
          changes.signed_dsf_mandate_note = '';
          this.updateView({ 'signed_dsf_mandate_note': '' });
        }
        if (changes.lender_agreement) {
          changes.lender_agreement_note = '';
          this.updateView({ 'lender_agreement_note': '' });
        }
        if (changes.supporting_correspondence_settlement) {
          changes.supporting_correspondence_settlement_note = '';
          this.updateView({ 'supporting_correspondence_settlement_note': '' });
        }
        if (changes.aml_clearance_or_screenshot) {
          changes.aml_clearance_or_screenshot_note = '';
          this.updateView({ 'aml_clearance_or_screenshot_note': '' });
        }
        if (changes.go_diligence_summary) {
          changes.go_diligence_summary_note = '';
          this.updateView({ 'go_diligence_summary_note': '' });
        }
        if (changes.dealfilechecklist_upload) {
          changes.dealfilechecklist_upload_note = '';
          this.updateView({ 'dealfilechecklist_upload_note': '' });
        }

        // Japan notes changes
        if (changes.disclosure_statement !== undefined && changes.disclosure_statement !== 'false') {
          changes.disclosure_statement_note = '';
          this.updateView({ 'disclosure_statement_note': '' });
        }
        if (changes.brokerage_agreement !== undefined && changes.brokerage_agreement !== 'false') {
          changes.brokerage_agreement_note = '';
          this.updateView({ 'brokerage_agreement_note': '' });
        }
        if (changes.service_contract !== undefined && changes.service_contract !== 'false') {
          changes.service_contract_note = '';
          this.updateView({ 'service_contract_note': '' });
        }
        if (changes.brokerage_commission_allocation_memorandum !== undefined &&
          changes.brokerage_commission_allocation_memorandum !== 'false') {
          changes.brokerage_commission_allocation_memorandum_note = '';
          this.updateView({ 'brokerage_commission_allocation_memorandum_note': '' });
        }
        if (changes.condition_precedent !== undefined && changes.condition_precedent !== 'true') {
          changes.condition_precedent_note = '';
          this.updateView({ 'condition_precedent_note': '' });
        }
        if (changes.japan_signed_agency_agreement) {
          changes.japan_signed_agency_agreement_note = '';
          this.updateView({ 'japan_signed_agency_agreement_note': '' });
        }
        if (changes.japan_supporting_documents_upload !== undefined &&
          changes.japan_supporting_documents_upload !== 'false') {
          changes.japan_supporting_documents_upload_note = '';
          this.updateView({ 'japan_supporting_documents_upload_note': '' });
        }

        // IND changes
        if (changes.india_conflict_of_interest_check === true) {
          changes.india_conflict_of_interest_check_note = '';
          this.updateView({ 'india_conflict_of_interest_check_note': '' });
        }
        if (changes.india_country_specific_company_check !== undefined &&
          changes.india_country_specific_company_check !== 'false') {
          changes.india_country_specific_company_check_note = '';
          this.updateView({ 'india_country_specific_company_check_note': '' });
        }
        if (changes.india_client_name_abn_agreement_invoice_match === true) {
          changes.india_client_name_abn_agreement_invoice_match_note = '';
          this.updateView({ 'india_client_name_abn_agreement_invoice_match_note': '' });
        }
        if (changes.india_aml_screening_process === false) {
          changes.india_aml_screening_process_note = '';
          this.updateView({ 'india_aml_screening_process_note': '' });
        }
        // china changes
        if (changes.service_deliverable === true && changes.service_deliverable !== undefined) {
          changes.service_deliverable_note = '';
          this.updateView({ 'service_deliverable_note': '' });
        }
        // Taiwan Changes
        if (changes.executed_lease_agreement !== undefined && changes.executed_lease_agreement === true) {
          changes.executed_lease_agreement_note = '';
          this.updateView({ 'executed_lease_agreement_note': '' });
        }
        if (changes.fee_earner_confirmation !== undefined && changes.fee_earner_confirmation === true) {
          changes.fee_earner_confirmation_note = '';
          this.updateView({ 'fee_earner_confirmation_note': '' });
        }
        if (changes.hasOwnProperty('mortgage_valuation_msa') && 
          changes.mortgage_valuation_msa !== this.data.mortgage_valuation_msa) {
          this.onSGPValuationDealFileChecklistChange(changes.mortgage_valuation_msa);
          return;
        }
        this.data = { ...this.data, ...changes };
        this.saveDealFileChecklist.emit(this.data);
      });
  }

  onSGPValuationDealFileChecklistChange(event) {
    if (event) {
      const msaDefaults = {
        conflict_of_interest: false,
        intermediary_between_client_and_government_entity: false,
        government_entity: null,
        go_listing: true,
        signed_agency_agreement: true,
        executed_lease_agreement: true,
        supporting_correspondence: true,
        fee_calculation_approval: 'true',
        supporting_documents_upload: true
      };
      this.data = { ...this.data, mortgage_valuation_msa: event, ...msaDefaults };
      this.updateView(msaDefaults);
      this.saveDealFileChecklist.emit(this.data);
    } else {
      this.data = { ...this.data, mortgage_valuation_msa: event };
      this.saveDealFileChecklist.emit(this.data);
    }
  }

  uploadFile(transactionId: number) {
    return this.transactionService.uploadTransactionFile(transactionId);
  }

  deleteFile(file: any) {
    this.transactionService.deleteTransactionFile(this.transaction.id, file.id)
      .pipe(untilDestroyed(this))
      .subscribe(() => this.loadTransactionEmitter.emit());
  }

  checkIdentifier(identifier: string): boolean {
    const identifiers = [
      'fee_calculation_approval',
      'disclosure_statement',
      'brokerage_agreement',
      'service_contract',
      'brokerage_commission_allocation_memorandum',
      'japan_supporting_documents_upload',
      'india_country_specific_company_check'
    ];
    return identifiers.includes(identifier);
  }

  toggleNoteField(value, identifier: string) {
    let returnValue: boolean;
    if (identifier === 'conflict_of_interest') {
      returnValue = value;
    } else if (identifier === 'condition_precedent' && !!value && value !== 'n/a') {
      returnValue = JSON.parse(value);
    } else if (this.checkIdentifier(identifier) && !!value && value !== 'n/a') {
      returnValue = JSON.parse(value) ? false : true;
    } else if (identifier === 'go_related_client' || identifier === 'japan_government_entity') {
      value === true ? returnValue = true : returnValue = false;
    } else if (identifier === 'intermediary_between_client_and_government_entity') {
      returnValue = false;
    } else if (identifier === 'india_aml_screening_process') {
      value === true ? returnValue = true : returnValue = false;
    }
    else {
      value === false ? returnValue = true : returnValue = false;
    }
    return returnValue;
  }

  download(file) {
    this.downloadFile.emit(file);
  }

  getQuestionsArray() {
    const transType = this.transaction.transaction_type;
    this.questionsArray = this.dealFileChecklistService.getQuestionsDealFileCheckList(transType, this.userCountry);
    if (this.transaction.transaction_type !== 'expense') {
      this.feeSplitService.getFeeSplits(this.transaction.id)
        .pipe(untilDestroyed(this))
        .subscribe(feeSplits => {
          this.feeSplits = feeSplits;
          this.questionsArray = this.getConjunctionalAgreementUploadedQuestion(this.feeSplits, this.questionsArray, transType, this.transaction.joint_agent);
          this.setupForm();
      });
    }
  }

  getConjunctionalAgreementUploadedQuestion(feeSplits: DataObject[], questionsArray: any[], transType: TransactionType, jointAgent: boolean = false) {
    const billExternalCheck = this.feeSplitService.billExternalCheck(feeSplits);
    this.isExternalBroker = this.feeSplitService.isExternalBroker(feeSplits);
    const conjunctional = (this.isExternalBroker && (this.userCountry === 'AUS' || this.userCountry === 'NZL')) ? 'external_conjunctional_agent' : 'conjunctional_agent';
    if (feeSplits && billExternalCheck && !jointAgent) {
      return this.dealFileChecklistService.getQuestionsDealFileCheckList(transType, this.userCountry, conjunctional);
    }
    return questionsArray;
  }

  updateView(fields: any, emitEvent = false) {
    this.dealFileChecklistForm.patchValue(fields, { emitEvent: emitEvent });
  }

  getTranslation(key: string): string {
    if (['AUS', 'NZL'].includes(this.userCountry) && this.isExternalBroker && key === 'conjunctional_agreement_uploaded') {
      return this.translateService.getTranslation(`EXTERNAL_${key.toUpperCase()}`)
    }
    const sgpSpecificKeys = ['supporting_documents_upload', 'conflict_of_interest', 'government_entity', 'conjunctional_agreement_uploaded'];
    if (['SGP', 'IDN'].includes(this.userCountry) && sgpSpecificKeys.includes(key)) {
      return this.translateService.getTranslation(`SGP_IDN_${key.toUpperCase()}`);
    }
    return this.translateService.getTranslation(key.toUpperCase());
  }

  saveTransactionFn(transaction: DataObject): void {
    this.questionsArray = this.getQuestionsBasedOnJointAgentCondition(transaction.joint_agent);
    this.setupForm();
    this.saveTransaction.emit(transaction);
  }

  getQuestionsBasedOnJointAgentCondition(jointAgent: boolean): DataObject[] {
    let questions = [];
    if (jointAgent === true) {
      questions = this.questionsArray.filter(question => question.identifier !== 'conjunctional_agreement_uploaded');
    } else {
      questions = this.getConjunctionalAgreementUploadedQuestion(this.feeSplits, this.questionsArray, this.transaction.transaction_type, jointAgent);
    }
    this.updateView({ 'conjunctional_agreement_uploaded': null, 'conjunctional_agreement_uploaded_note': '' }, true);
    return questions;
  }

  ngOnDestroy() {
  }

}
